import Dexie, { Table } from 'dexie';

export interface QueuedRequest {
  id?: number;
  url: string;
  method: string;
  data?: any;
  timestamp: Date;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  response?: any;
  error?: any;
  retryCount?: number;
}

export interface CachedSession {
  id: string;
  job_no: string;
  activity_code: string;
  started_at: string;
  ended_at?: string;
  note?: string;
  unique_key: string;
  synced: boolean;
}

export interface CachedMedia {
  id: string;
  session_id: string;
  file_data: Blob;
  captured_at: string;
  sha256: string;
  synced: boolean;
}

export interface PendingMedia {
  id?: number;
  sessionId: string;
  file: Blob;
  fileName: string;
  capturedAt: string;
  sha256?: string;
  status: 'pending' | 'uploading' | 'failed' | 'synced';
  retryCount: number;
  error?: string;
  createdAt: Date;
  lastRetry?: Date;
}

export interface RecentJob {
  job_no: string;
  description?: string;
  customer?: string;
  last_used: Date;
}

class TechnicianActivityDB extends Dexie {
  queuedRequests!: Table<QueuedRequest>;
  cachedSessions!: Table<CachedSession>;
  cachedMedia!: Table<CachedMedia>;
  pendingMedia!: Table<PendingMedia>;
  recentJobs!: Table<RecentJob>;

  constructor() {
    super('TechnicianActivityDB');
    
    this.version(2).stores({
      queuedRequests: '++id, status, timestamp',
      cachedSessions: 'id, unique_key, synced, started_at',
      cachedMedia: 'id, session_id, synced',
      pendingMedia: '++id, sessionId, status, createdAt',
      recentJobs: 'job_no, last_used',
    });
  }
}

export const db = new TechnicianActivityDB();

// Helper functions for offline operations
export const offlineHelpers = {
  // Queue a request for later sync
  queueRequest: async (url: string, method: string, data?: any) => {
    const request: Omit<QueuedRequest, 'id'> = {
      url,
      method,
      data,
      timestamp: new Date(),
      status: 'pending',
      retryCount: 0,
    };
    
    return await db.queuedRequests.add(request);
  },

  // Save session locally
  saveSessionLocally: async (session: Omit<CachedSession, 'id'>) => {
    const sessionWithId: CachedSession = {
      id: generateId(),
      ...session,
      synced: false,
    };
    return await db.cachedSessions.add(sessionWithId);
  },

  // Save media locally
  saveMediaLocally: async (sessionId: string, file: File) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const sha256 = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    const media: CachedMedia = {
      id: generateId(),
      session_id: sessionId,
      file_data: new Blob([buffer], { type: file.type }),
      captured_at: new Date().toISOString(),
      sha256,
      synced: false,
    };
    
    return await db.cachedMedia.add(media);
  },

  // Update recent jobs
  updateRecentJob: async (job: Omit<RecentJob, 'last_used'>) => {
    const existing = await db.recentJobs.get(job.job_no);
    
    if (existing) {
      await db.recentJobs.update(job.job_no, {
        ...job,
        last_used: new Date(),
      });
    } else {
      await db.recentJobs.add({
        ...job,
        last_used: new Date(),
      });
    }
    
    // Keep only the 10 most recent jobs
    const allJobs = await db.recentJobs.orderBy('last_used').reverse().toArray();
    if (allJobs.length > 10) {
      const toDelete = allJobs.slice(10);
      for (const job of toDelete) {
        await db.recentJobs.delete(job.job_no);
      }
    }
  },

  // Get recent jobs
  getRecentJobs: async (limit: number = 5) => {
    return await db.recentJobs
      .orderBy('last_used')
      .reverse()
      .limit(limit)
      .toArray();
  },

  // Cache jobs for offline use
  cacheJobs: async (jobs: any[]) => {
    // Store jobs in localStorage for quick access
    localStorage.setItem('cached_jobs', JSON.stringify(jobs));
  },

  // Get cached jobs
  getCachedJobs: async () => {
    const cached = localStorage.getItem('cached_jobs');
    return cached ? JSON.parse(cached) : [];
  },

  // Cache activities for offline use
  cacheActivities: async (activities: any[]) => {
    localStorage.setItem('cached_activities', JSON.stringify(activities));
  },

  // Get cached activities
  getCachedActivities: async () => {
    const cached = localStorage.getItem('cached_activities');
    return cached ? JSON.parse(cached) : [];
  },

  // Sync queue using the shared Axios instance (respects baseURL and auth interceptors)
  syncQueue: async () => {
    const { api } = await import('./api');
    const requests = await db.queuedRequests.where('status').equals('pending').toArray();
    
    for (const request of requests) {
      try {
        await db.queuedRequests.update(request.id!, { status: 'syncing' });

        const response = await api({
          method: request.method as any,
          url: request.url,
          data: request.data,
        });

        await db.queuedRequests.update(request.id!, { 
          status: 'synced',
          response: response.data,
        });
      } catch (error: any) {
        console.error('Failed to sync request:', error);
        const status = error.response?.status;
        if (status === 401) {
          await db.queuedRequests.update(request.id!, { status: 'pending' });
        } else if (status && status >= 400 && status < 500) {
          await db.queuedRequests.update(request.id!, { 
            status: 'failed',
            retryCount: (request.retryCount || 0) + 1,
          });
        } else {
          await db.queuedRequests.update(request.id!, { 
            status: 'pending',
            retryCount: (request.retryCount || 0) + 1,
          });
        }
      }
    }
  },

  // Get pending requests count
  getPendingRequestsCount: async () => {
    return await db.queuedRequests.where('status').equals('pending').count();
  },

  // Clear all cached data
  clearAllCache: async () => {
    await db.queuedRequests.clear();
    await db.cachedSessions.clear();
    await db.cachedMedia.clear();
    await db.pendingMedia.clear();
    await db.recentJobs.clear();
    localStorage.removeItem('cached_jobs');
    localStorage.removeItem('cached_activities');
  },
};

// Helper function to generate unique ID
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default db;
